<template>
  <div
    id="header-burger"
    class="header-burger"
    :class="headerBurgerClass"
    arai-label="Выпадающее меню"
    @mouseleave="$emit('leave')"
  >
    <button
      aria-labelledby="header-burger"
      class="header-burger__button"
      @click="openBurgerMenu"
    >
      <span class="header-burger__button-elem" />
    </button>
    <component
      :is="tag"
      class="header-burger__main-link"
      :href="isMainPage ? undefined : '/'"
      :aria-label="isMainPage ? undefined : 'Перейти на главную страницу'"
      @click="sendAnalytics({ link: '/' }, 'Лого')"
    >
      <!-- ЛОГО -->
      <!-- в header-burger__logo-custom находятся тематические лого -->
      <!-- <div class="header-burger__logo-custom" /> -->
      <SvgAsset
        class="header-burger__logo"
        :content="logoSvg"
      />
    </component>
    <HeaderBurgerMenu
      class="header-burger__menu"
      @click-menu-item="sendAnalytics($event, 'Бургер-меню')"
    />
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import SvgAsset from '@devhacker/shared/components/SvgAsset.vue';
  import HeaderBurgerMenu from '~/components/TheHeader/Burger/HeaderBurgerMenu.vue';
  import BHLink from '~/components/BHLink.vue';

  import logoSvg from '~/assets/images/svg/logo.svg?raw';
  import logoOctoberSvg from '~/assets/images/svg/logo-october.svg?raw';
  import { isOctober2026Logo } from '~/constants';

  import { lockScroll, unlockScroll } from '@devhacker/shared/utils/dom/scrollLock.ts';
  import { useWindowStore } from '~/store/window';

  export default defineNuxtComponent({
    name: 'HeaderBurger',

    components: { SvgAsset, HeaderBurgerMenu, BHLink },

    inject: {
      // Категория в которой происходит событие
      // Для аналитики
      eventCategory: {
        default() {
          return () => '';
        },
      },
    },

    props: {
      burgerActive: {
        type: Boolean as PropType<boolean>,
        required: true,
      },
    },

    emits: ['leave', 'toggle-burger-menu'],

    computed: {
      logoSvg(): string {
        return isOctober2026Logo() ? logoOctoberSvg : logoSvg;
      },

      isMobile() {
        return useWindowStore().isMobile;
      },

      isMainPage() {
        return useRoute().path === '/';
      },

      tag() {
        return this.isMainPage ? 'div' : 'a';
      },

      headerBurgerClass() {
        const className = 'header-burger';
        return {
          [`${className}--active`]: this.burgerActive,
        };
      },
    },

    watch: {
      burgerActive(burgerActive) {
        if (burgerActive && this.isMobile) {
          return lockScroll();
        } else if (!burgerActive && this.isMobile) {
          unlockScroll();
        }
      },
    },

    methods: {
      openBurgerMenu(): void {
        this.$emit('toggle-burger-menu');
      },

      sendAnalytics(event: { link: { title: string }; event?: string }, _element: string) {
        this.$sendYandexMetrika({
          level1: event?.event || _element,
          level5: event.link.title,
        });
      },
    },
  });
</script>

<style lang="scss" scoped>
  @use '../styles/TheHeader.variables.scss' as *;
  @use '../styles/HeaderBurger.mixins' as *;

  $active-state-delay: 1s;

  .header-burger {
    position: relative;
    padding-left: $spacing-3;
    display: flex;
    justify-content: space-between;
    align-items: center;

    @include tablet {
      min-height: $headerHeight;
      justify-content: flex-start;
    }

    @include big-tablet {
      flex-direction: row-reverse;
      justify-content: flex-end;
      padding-left: $spacing-6;
      min-width: 280px;
    }

    @include hover {
      @include active-state__burger;

      @include big-tablet {
        .header-burger__logo-custom::before {
          opacity: 1;
          transition-delay: initial;
          transition-duration: initial;
        }
      }
    }

    &::before {
      width: 50vw;
      position: fixed;
      height: $headerHeight;
      top: 0;
      opacity: 0;
      content: '';
      background: $headerBg;
      left: 0;
      z-index: 3;
      pointer-events: none;
      border-bottom: 1px solid $stroke-color--1;
      transition: opacity 0.1s ease;
      transition-delay: 0.3s;
      @include tablet {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: unset;
        border: 1px solid $headerActiveBurgerBorder;
        border-bottom: none;
        box-shadow: boxShadow((transparent, transparent, $headerBorder), (1px));
        @include header-burger-transition(opacity);

        @include no-touch-device {
          transition-delay: $active-state-delay;
        }
      }
    }

    &--active {
      .header-burger {
        @include active-state__button;
        @include active-state__menu;
      }

      @include big-tablet {
        .header-burger {
          @include active-state__logo;
        }
      }

      @include active-state__before;
    }

    &__main-link {
      width: 129px;
      height: 20px;
      display: block;
      z-index: 3;

      @include big-tablet() {
        width: 194px;
        height: 30px;
      }
    }

    &__button {
      width: 24px;
      height: 24px;
      display: block;
      margin-right: $spacing-4;
      z-index: 3;
      position: relative;
      @include burger-elems;

      @include big-tablet() {
        margin-right: 0;
        margin-left: $spacing-4;
      }
    }

    &__logo {
      color: black;
      width: 100%;
      height: 100%;

      @include header-burger-transition(color);
      @include no-touch-device {
        transition-delay: $active-state-delay;
      }
    }

    &__logo-custom {
      position: relative;
      //background: no-repeat url('@/assets/images/svg/helloween-black.svg');
      /* background: no-repeat url('@/assets/images/svg/logo-new-year-black.svg'); */
      height: 100%;
      width: 100%;
      background-size: cover;

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
        // background: no-repeat url('@/assets/images/svg/helloween-pink.svg');
        /* background: no-repeat url('@/assets/images/svg/logo-new-year-purple.svg'); */
        background-size: cover;
        opacity: 0;

        @include header-burger-transition(opacity);
        @include no-touch-device {
          transition-delay: $active-state-delay;
        }
      }
    }

    &__menu {
      transform: translateY(-5%);
      position: fixed;
      width: 100%;
      height: calc(100% - $headerHeight);
      top: $headerHeight;
      opacity: 0;
      pointer-events: none;
      left: 0;
      @include header-burger-transition(all);
      background: $headerBg;
      z-index: 2;

      @include tablet {
        transform: unset;
        position: absolute;
        height: calc(100vh - $headerHeight);

        @include no-touch-device {
          transition-delay: $active-state-delay;
        }
      }

      @include big-tablet {
        min-width: 280px;
      }
    }
  }
</style>
